import { useEffect, ReactNode } from "react";
import { useBranding } from "@/hooks/useBranding";
import { useTheme } from "next-themes";

/* ── Preset definitions ── */
interface PresetTokens {
  primary: string;
  glow: string;
  glowSecondary: string;
  hue: number;
}

const PRESETS: Record<string, PresetTokens> = {
  neon_blue: { primary: "252 100% 65%", glow: "252 100% 65%", glowSecondary: "280 80% 60%", hue: 252 },
  emerald_cyan: { primary: "160 84% 45%", glow: "160 84% 45%", glowSecondary: "180 70% 50%", hue: 160 },
  red_magenta: { primary: "340 82% 55%", glow: "340 82% 55%", glowSecondary: "310 75% 55%", hue: 340 },
  orange_gold: { primary: "30 95% 55%", glow: "30 95% 55%", glowSecondary: "45 90% 50%", hue: 30 },
  mono_steel: { primary: "220 15% 55%", glow: "220 15% 55%", glowSecondary: "220 10% 45%", hue: 220 },
};

function applyPreset(preset: PresetTokens) {
  const h = preset.hue;
  const styleId = "brand-hue-override";
  let el = document.getElementById(styleId) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement("style");
    el.id = styleId;
    document.head.appendChild(el);
  }

  el.textContent = `
    :root {
      --background: ${h > 150 && h < 200 ? h : 230} 25% 5%;
      --foreground: 220 15% 93%;
      --card: ${h > 150 && h < 200 ? h : 230} 20% 9%;
      --card-foreground: 220 15% 93%;
      --popover: ${h > 150 && h < 200 ? h : 230} 20% 9%;
      --popover-foreground: 220 15% 93%;
      --primary: ${preset.primary};
      --primary-foreground: 0 0% 100%;
      --secondary: ${h} 30% 16%;
      --secondary-foreground: ${h} 15% 80%;
      --muted: ${h > 150 && h < 200 ? h : 230} 20% 13%;
      --muted-foreground: 220 10% 55%;
      --accent: ${h} 30% 16%;
      --accent-foreground: ${h} 15% 80%;
      --border: ${h > 150 && h < 200 ? h : 230} 15% 16%;
      --input: ${h > 150 && h < 200 ? h : 230} 15% 16%;
      --ring: ${preset.primary};
      --glow: ${preset.glow};
      --glow-secondary: ${preset.glowSecondary};
      --surface-glass: ${h > 150 && h < 200 ? h : 230} 20% 12%;
      --sidebar-background: ${h > 150 && h < 200 ? h : 230} 22% 7%;
      --sidebar-foreground: 220 15% 93%;
      --sidebar-primary: ${preset.primary};
      --sidebar-primary-foreground: 0 0% 100%;
      --sidebar-accent: ${h} 30% 14%;
      --sidebar-accent-foreground: ${h} 15% 80%;
      --sidebar-border: ${h > 150 && h < 200 ? h : 230} 15% 16%;
      --sidebar-ring: ${preset.primary};
    }
    .light {
      --background: ${h} 100% 98%;
      --foreground: ${h} 30% 10%;
      --card: 0 0% 100%;
      --card-foreground: ${h} 30% 10%;
      --popover: 0 0% 100%;
      --popover-foreground: ${h} 30% 10%;
      --primary: ${preset.primary};
      --primary-foreground: 0 0% 100%;
      --secondary: ${h} 60% 95%;
      --secondary-foreground: ${h} 80% 45%;
      --muted: ${h} 30% 94%;
      --muted-foreground: ${h} 10% 45%;
      --accent: ${h} 60% 95%;
      --accent-foreground: ${h} 80% 45%;
      --border: ${h} 20% 91%;
      --input: ${h} 20% 91%;
      --ring: ${preset.primary};
      --glow: ${preset.glow};
      --glow-secondary: ${preset.glowSecondary};
      --surface-glass: ${h} 30% 96%;
      --sidebar-background: 0 0% 100%;
      --sidebar-foreground: ${h} 30% 10%;
      --sidebar-primary: ${preset.primary};
      --sidebar-primary-foreground: 0 0% 100%;
      --sidebar-accent: ${h} 60% 95%;
      --sidebar-accent-foreground: ${h} 80% 45%;
      --sidebar-border: ${h} 20% 91%;
      --sidebar-ring: ${preset.primary};
    }
  `;
}

export default function BrandingProvider({ children }: { children: ReactNode }) {
  const { appName, faviconUrl, faviconLightUrl, ogImageUrl, themePreset, primaryHue } = useBranding();
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    document.title = `${appName} — Crypto, P2P, Notes & Futures`;
  }, [appName]);

  useEffect(() => {
    const isDark = resolvedTheme === "dark";
    const url = isDark ? faviconUrl : faviconLightUrl;
    if (url) {
      let link = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = url;
    }
  }, [faviconUrl, faviconLightUrl, resolvedTheme]);

  useEffect(() => {
    if (ogImageUrl) {
      const setMeta = (selector: string, attr: string, value: string) => {
        let el = document.querySelector(selector) as HTMLMetaElement | null;
        if (el) el.setAttribute(attr, value);
      };
      setMeta("meta[property='og:image']", "content", ogImageUrl);
      setMeta("meta[name='twitter:image']", "content", ogImageUrl);
    }
  }, [ogImageUrl]);

  useEffect(() => {
    const preset = PRESETS[themePreset];
    if (preset) {
      applyPreset(preset);
    } else if (primaryHue !== null && primaryHue !== undefined) {
      // Fallback to custom hue
      const fallback: PresetTokens = {
        primary: `${primaryHue} 100% 65%`,
        glow: `${primaryHue} 100% 65%`,
        glowSecondary: `${(primaryHue + 28) % 360} 80% 60%`,
        hue: primaryHue,
      };
      applyPreset(fallback);
    }
  }, [themePreset, primaryHue]);

  return <>{children}</>;
}
